import type { PortalState, ReportVersion } from "./domain";

export const WORKSPACE_PRIMARY_KEY = "mo-local-portal-v2";
export const WORKSPACE_SCHEMA_VERSION = "portal-v2-2026-07-29";
export const WORKSPACE_BACKUP_PREFIX = "mo-local-portal-recovery-backup-";
export const WORKSPACE_MIGRATION_BACKUP_PREFIX = "mo-local-portal-migration-backup-";
export const WORKSPACE_STAGING_PREFIX = "mo-local-portal-staging-";

export interface WorkspaceStorage {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface WorkspaceCounts {
  organisations: number;
  audits: number;
  reports: number;
  versions: number;
  sectionTenIdentities: string[];
}

export interface WorkspaceCandidate {
  raw: string;
  parsed: Partial<PortalState>;
  migrated: PortalState;
  migratedRaw: string;
  needsMigration: boolean;
  before: WorkspaceCounts;
  after: WorkspaceCounts;
}

export type WorkspaceStartup =
  | { kind: "new-demonstration"; state: PortalState; autosaveAllowed: true; message: string }
  | { kind: "valid"; state: PortalState; autosaveAllowed: true; raw: string; needsMigration: false }
  | { kind: "migration-pending"; state: PortalState; autosaveAllowed: false; raw: string; needsMigration: true }
  | { kind: "recovery-required"; state: PortalState; autosaveAllowed: false; raw?: string; reason: "Invalid JSON" | "Migration failure" | "Validation failure"; error: string };

export interface WorkspaceExportSource {
  key: string;
  rawValue: string;
  byteLength: number;
  sha256: string;
}

export interface WorkspaceExportSummary {
  key: string;
  byteLength: number;
  sha256: string;
  parseStatus: "Valid JSON" | "Invalid JSON";
  counts?: WorkspaceCounts;
}

export interface IndexedDbInventory {
  supported: boolean;
  databases: Array<{ name: string; version?: number; fileKeys?: string[]; error?: string }>;
}

export interface WorkspaceExport {
  manifest: {
    createdAt: string;
    origin: string;
    schemaVersion: string;
    primaryKey: string;
    summaries: WorkspaceExportSummary[];
    indexedDb: IndexedDbInventory;
  };
  sources: WorkspaceExportSource[];
}

const REQUIRED_ARRAYS: Array<keyof PortalState> = [
  "organisations", "decisions", "platforms", "platformConfigurations", "prompts", "limitations",
  "actions", "reports", "audits", "notifications", "activities", "clientDetails", "contacts",
  "onboardingWorkflows", "ayzeoPdfSnapshots", "ownedChannels", "mentionKeywords", "ayzeoProjectLinks",
  "ayzeoCollectionBatches", "ayzeoPromptPlatformRuns", "ayzeoPdfPromptSnapshots", "ayzeoMetricSnapshots",
  "ayzeoReconciliations", "ayzeoSupportingSnapshots", "brandOccurrences", "rehearsalFindings",
  "reportPdfExports", "reportManualDeliveries", "workspaceBackupRecords",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function validateWorkspaceShape(value: unknown): asserts value is Partial<PortalState> {
  if (!isRecord(value)) throw new Error("The saved workspace is not an object.");
  for (const key of REQUIRED_ARRAYS) {
    if (key in value && !Array.isArray(value[key])) throw new Error(`The saved workspace field "${String(key)}" is not a list.`);
  }
  if (!Array.isArray(value.organisations) || !Array.isArray(value.reports) || !Array.isArray(value.audits)) {
    throw new Error("The saved workspace is missing its core organisation, audit or report lists.");
  }
}

function locked(version: ReportVersion) {
  return ["Approved", "Published", "Superseded", "Archived"].includes(version.status);
}

export function assertLockedVersionsUnchanged(before: Partial<PortalState>, after: PortalState) {
  const nextVersions = new Map(after.reports.flatMap(report => report.versions.map(version => [version.id, version] as const)));
  for (const report of before.reports ?? []) {
    for (const version of report.versions ?? []) {
      if (!locked(version)) continue;
      const migrated = nextVersions.get(version.id);
      if (!migrated || JSON.stringify(migrated) !== JSON.stringify(version)) {
        throw new Error(`Migration would change locked report version "${version.id}".`);
      }
    }
  }
}

function collectSectionTenIdentities(value: unknown, output = new Set<string>()): Set<string> {
  if (Array.isArray(value)) value.forEach(item => collectSectionTenIdentities(item, output));
  else if (isRecord(value)) {
    for (const [key, child] of Object.entries(value)) {
      if ((key === "id" || /identity|sourceId|draftId/i.test(key)) && typeof child === "string" && child.toLowerCase().includes("section10")) output.add(child);
      collectSectionTenIdentities(child, output);
    }
  }
  return output;
}

export function workspaceCounts(value: Partial<PortalState>): WorkspaceCounts {
  return {
    organisations: value.organisations?.length ?? 0,
    audits: value.audits?.length ?? 0,
    reports: value.reports?.length ?? 0,
    versions: value.reports?.reduce((total, report) => total + (report.versions?.length ?? 0), 0) ?? 0,
    sectionTenIdentities: [...collectSectionTenIdentities(value)].sort(),
  };
}

export function validateMigratedWorkspace(value: PortalState) {
  validateWorkspaceShape(value);
  const organisationIds = new Set(value.organisations.map(item => item.id));
  const duplicateOrganisation = value.organisations.find((item, index) => value.organisations.findIndex(other => other.id === item.id) !== index);
  if (duplicateOrganisation) throw new Error(`Duplicate organisation identity "${duplicateOrganisation.id}".`);
  const duplicateReport = value.reports.find((item, index) => value.reports.findIndex(other => other.id === item.id) !== index);
  if (duplicateReport) throw new Error(`Duplicate report identity "${duplicateReport.id}".`);
  const orphan = value.reports.find(report => !organisationIds.has(report.organisationId));
  if (orphan) throw new Error(`Report "${orphan.id}" does not belong to a stored organisation.`);
  for (const report of value.reports) {
    const versionIds = new Set<string>();
    for (const version of report.versions) {
      if (versionIds.has(version.id)) throw new Error(`Duplicate report version identity "${version.id}".`);
      versionIds.add(version.id);
    }
    if (report.displayedVersionId && !versionIds.has(report.displayedVersionId)) {
      throw new Error(`Report "${report.id}" points to a missing displayed version.`);
    }
  }
}

export function parseWorkspaceCandidate(raw: string, migrate: (saved: Partial<PortalState>) => PortalState): WorkspaceCandidate {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (reason) {
    throw new Error(`Invalid JSON: ${reason instanceof Error ? reason.message : "The saved value could not be parsed."}`);
  }
  validateWorkspaceShape(parsed);
  let migrated: PortalState;
  try {
    migrated = migrate(parsed);
  } catch (reason) {
    throw new Error(`Migration failure: ${reason instanceof Error ? reason.message : "The saved value could not be migrated."}`);
  }
  try {
    validateMigratedWorkspace(migrated);
    assertLockedVersionsUnchanged(parsed, migrated);
  } catch (reason) {
    throw new Error(`Validation failure: ${reason instanceof Error ? reason.message : "The migrated value was not safe."}`);
  }
  const migratedRaw = JSON.stringify(migrated);
  return {
    raw,
    parsed,
    migrated,
    migratedRaw,
    needsMigration: migratedRaw !== raw,
    before: workspaceCounts(parsed),
    after: workspaceCounts(migrated),
  };
}

export function inspectWorkspaceStartup(storage: WorkspaceStorage, seed: PortalState, migrate: (saved: Partial<PortalState>) => PortalState): WorkspaceStartup {
  const raw = storage.getItem(WORKSPACE_PRIMARY_KEY);
  if (raw === null) {
    return {
      kind: "new-demonstration",
      state: seed,
      autosaveAllowed: true,
      message: "This is a new demonstration workspace. No saved client workspace was loaded.",
    };
  }
  try {
    const candidate = parseWorkspaceCandidate(raw, migrate);
    return candidate.needsMigration
      ? { kind: "migration-pending", state: candidate.migrated, autosaveAllowed: false, raw, needsMigration: true }
      : { kind: "valid", state: candidate.migrated, autosaveAllowed: true, raw, needsMigration: false };
  } catch (reason) {
    const message = reason instanceof Error ? reason.message : "The saved workspace could not be loaded.";
    const type = message.startsWith("Invalid JSON") ? "Invalid JSON" : message.startsWith("Migration failure") ? "Migration failure" : "Validation failure";
    return { kind: "recovery-required", state: seed, autosaveAllowed: false, raw, reason: type, error: message };
  }
}

export async function sha256Text(value: string) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map(item => item.toString(16).padStart(2, "0")).join("").toUpperCase();
}

function timestampKey(date = new Date()) {
  return date.toISOString().replace(/[^0-9]/g, "");
}

export async function createChecksummedBackup(storage: WorkspaceStorage, raw: string, prefix = WORKSPACE_MIGRATION_BACKUP_PREFIX, date = new Date()) {
  const key = `${prefix}${timestampKey(date)}`;
  const sha256 = await sha256Text(raw);
  storage.setItem(key, raw);
  storage.setItem(`${key}-manifest`, JSON.stringify({ key, byteLength: new TextEncoder().encode(raw).byteLength, sha256, createdAt: date.toISOString(), sourceKey: WORKSPACE_PRIMARY_KEY }));
  return { key, sha256 };
}

export async function stageWorkspaceCandidate(storage: WorkspaceStorage, raw: string, sourceName: string, date = new Date()) {
  const key = `${WORKSPACE_STAGING_PREFIX}${timestampKey(date)}`;
  const sha256 = await sha256Text(raw);
  storage.setItem(key, raw);
  storage.setItem(`${key}-manifest`, JSON.stringify({ key, sourceName, byteLength: new TextEncoder().encode(raw).byteLength, sha256, createdAt: date.toISOString() }));
  return { key, sha256 };
}

export async function validateStagedWorkspace(storage: WorkspaceStorage, key: string, migrate: (saved: Partial<PortalState>) => PortalState) {
  const raw = storage.getItem(key);
  if (raw === null) throw new Error("The staged workspace is no longer available.");
  const candidate = parseWorkspaceCandidate(raw, migrate);
  const validatedKey = `${key}-validated`;
  const sha256 = await sha256Text(candidate.migratedRaw);
  storage.setItem(validatedKey, candidate.migratedRaw);
  storage.setItem(`${validatedKey}-manifest`, JSON.stringify({ sourceKey: key, sha256, before: candidate.before, after: candidate.after, validatedAt: new Date().toISOString() }));
  return { ...candidate, key, validatedKey, sha256 };
}

export async function promoteStagedWorkspace(storage: WorkspaceStorage, validatedKey: string, date = new Date()) {
  const staged = storage.getItem(validatedKey);
  if (staged === null) throw new Error("Validate the candidate before using it.");
  const validationManifestRaw = storage.getItem(`${validatedKey}-manifest`);
  if (validationManifestRaw === null) throw new Error("The staged workspace has no validation record.");
  const validationManifest = JSON.parse(validationManifestRaw) as { sha256?: string };
  const stagedSha256 = await sha256Text(staged);
  if (!validationManifest.sha256 || validationManifest.sha256 !== stagedSha256) throw new Error("The staged workspace no longer matches its validation checksum.");
  validateWorkspaceShape(JSON.parse(staged));
  const current = storage.getItem(WORKSPACE_PRIMARY_KEY);
  let backup: { key: string; sha256: string } | undefined;
  if (current !== null) backup = await createChecksummedBackup(storage, current, WORKSPACE_BACKUP_PREFIX, date);
  storage.setItem(WORKSPACE_PRIMARY_KEY, staged);
  return { backup, primarySha256: stagedSha256 };
}

/** Reads the exact primary value written by a completed staged activation. */
export function readActivatedWorkspace(storage: WorkspaceStorage, migrate: (saved: Partial<PortalState>) => PortalState) {
  const raw = storage.getItem(WORKSPACE_PRIMARY_KEY);
  if (raw === null) throw new Error("The activated workspace is no longer available.");
  return migrate(JSON.parse(raw) as Partial<PortalState>);
}

export async function restorePreviousWorkspace(storage: WorkspaceStorage, backupKey: string) {
  const raw = storage.getItem(backupKey);
  if (raw === null) throw new Error("The selected previous workspace is no longer available.");
  const expectedManifest = storage.getItem(`${backupKey}-manifest`);
  const expected = expectedManifest ? (JSON.parse(expectedManifest) as { sha256?: string }).sha256 : undefined;
  const actual = await sha256Text(raw);
  if (expected && expected !== actual) throw new Error("The previous workspace checksum does not match.");
  storage.setItem(WORKSPACE_PRIMARY_KEY, raw);
  return { sha256: actual };
}

export function relatedWorkspaceKeys(storage: WorkspaceStorage) {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith("mo-local-portal")) keys.push(key);
  }
  return keys.sort();
}

export async function inventoryIndexedDb(): Promise<IndexedDbInventory> {
  if (typeof indexedDB === "undefined" || typeof indexedDB.databases !== "function") return { supported: false, databases: [] };
  const databases = await indexedDB.databases();
  const output: IndexedDbInventory["databases"] = [];
  for (const database of databases) {
    if (!database.name) continue;
    const item: IndexedDbInventory["databases"][number] = { name: database.name, version: database.version };
    if (database.name === "magnum-opus-local-files") {
      try {
        item.fileKeys = await new Promise<string[]>((resolve, reject) => {
          const request = indexedDB.open(database.name!, database.version);
          request.onerror = () => reject(request.error);
          request.onsuccess = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains("files")) { db.close(); resolve([]); return; }
            const transaction = db.transaction("files", "readonly");
            const keys = transaction.objectStore("files").getAllKeys();
            keys.onerror = () => reject(keys.error);
            keys.onsuccess = () => { db.close(); resolve(keys.result.map(String)); };
          };
        });
      } catch (reason) {
        item.error = reason instanceof Error ? reason.message : "File keys could not be listed.";
      }
    }
    output.push(item);
  }
  return { supported: true, databases: output };
}

export async function createWorkspaceExport(storage: WorkspaceStorage, origin: string, indexedDb = inventoryIndexedDb()): Promise<WorkspaceExport> {
  const sources: WorkspaceExportSource[] = [];
  const summaries: WorkspaceExportSummary[] = [];
  for (const key of relatedWorkspaceKeys(storage)) {
    const rawValue = storage.getItem(key);
    if (rawValue === null) continue;
    const byteLength = new TextEncoder().encode(rawValue).byteLength;
    const sha256 = await sha256Text(rawValue);
    const source = { key, rawValue, byteLength, sha256 };
    sources.push(source);
    try {
      const parsed = JSON.parse(rawValue) as Partial<PortalState>;
      summaries.push({ key, byteLength, sha256, parseStatus: "Valid JSON", counts: workspaceCounts(parsed) });
    } catch {
      summaries.push({ key, byteLength, sha256, parseStatus: "Invalid JSON" });
    }
  }
  return {
    manifest: {
      createdAt: new Date().toISOString(),
      origin,
      schemaVersion: WORKSPACE_SCHEMA_VERSION,
      primaryKey: WORKSPACE_PRIMARY_KEY,
      summaries,
      indexedDb: await indexedDb,
    },
    sources,
  };
}
